import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { has, pick, omit, sortBy } from 'lodash-es';
import { useSelector, useDispatch } from 'react-redux';
import { pathOr, isEmpty } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';
import hash from 'object-hash';
import { useTranslation } from 'react-i18next';
import { useCurrentWidth } from 'effects';
import Label from 'pureUi/Label';
import Checkbox from 'pureUi/Checkbox';
import { formatDate } from 'utils';
import * as DateFNS from 'date-fns';
import * as AgentsSelectors from 'store/modules/agents/selectors';
import * as RoomsStyles from './Rooms.styles';

import AccommodationCard from 'components/AccommodationCard';
import { getFromPrices } from 'components/AccommodationCard/utils';
import { ENetworkRequestStatus } from 'services/BackendApi';
import { theme } from '../../../tailwind.config';
import * as HotelAccommodationProductActions from 'store/modules/hotelAccommodationProducts/actions';
import * as BookingBuilderActions from 'store/modules/bookingBuilder/actions';

import * as BookingBuilderSelectors from 'store/modules/bookingBuilder/selectors';
import * as AuthSelectors from 'store/modules/auth/selectors';
import * as HotelAccommodationProductSelectors from 'store/modules/hotelAccommodationProducts/selectors';
import * as FastSearchSelectors from 'store/modules/fastSearch/selectors';
import * as SearchSelectors from 'store/modules/bookingBuilder/subdomains/search/selectors';
import * as CompanyInfoSelectors from 'store/modules/companyInfo/selectors';
import * as BookingsSelectors from 'store/modules/bookings/selectors';
import {
  IHAPFormattedAccommodationProduct,
  IHAPFormattedAccommodationProductWithLiveRatesWithBookingComRates,
} from 'store/modules/hotelAccommodationProducts/types';
import { BasketRateTypes, TCurrencyCode } from 'interfaces';
import { Multiselect } from 'ui/Multiselect';
import * as Sentry from '@sentry/browser';
import * as StaticRatesAvailabilityActions from 'store/modules/staticRatesAvailability/actions';
import * as StaticRatesAvailabilitySelectors from 'store/modules/staticRatesAvailability/selectors';
import { Loader } from 'components/Loader/Loader';
import { useCurrencyConversion } from 'context/CurrencyConversion/CurrencyConversionContext';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { getCountryCodeForCurrency } from 'utils/currencyConversion';
import Flag from 'react-world-flags';
import { IMultiselectValueLabelPair } from 'ui/Multiselect';

export const filterRoomsByCategoryType = (rooms, categoryTypes) => {
  if (!Array.isArray(categoryTypes)) {
    categoryTypes = [categoryTypes];
  }

  if (categoryTypes.length <= 0) {
    return rooms;
  }

  return rooms.filter(room => {
    return categoryTypes.includes(room.categoryType);
  });
};

export const numbersInArrayBetweenRange = (nums, from, to) => {
  return nums.reduce((count, num) => {
    if (num >= from && num <= to) {
      count++;
    }
    return count;
  }, 0);
};

export const doGuestAgesFitInsideLodging = (guestAges, lodgingOccupancy, ages) => {
  const totalGuests = guestAges.numberOfAdults + guestAges.agesOfAllChildren.length;

  // first - is there too many people in total
  if (totalGuests > lodgingOccupancy.maximumPeople) {
    return false;
  }

  // get all the ageRanges and their counts into an object
  const ageBrackets = {
    default: guestAges.numberOfAdults,
  };
  ages.forEach(ageBracket => {
    ageBrackets[ageBracket.name] = numbersInArrayBetweenRange(
      guestAges.agesOfAllChildren,
      ageBracket.ageFrom,
      ageBracket.ageTo
    );
  });

  // for instead of forEach so we can do the return neatly
  for (let i = 0; i < lodgingOccupancy.limits.length; i++) {
    const limit = lodgingOccupancy.limits[i];
    const ageBracketForThisLimit = ageBrackets[limit.name];
    if (ageBracketForThisLimit < limit.minimum || ageBracketForThisLimit > limit.maximum) {
      return false;
    }
  }

  return true;
};

const mergeAccommodationProductData = (
  staticRates,
  liveRates,
  onlyShowLiveRates,
  bookingComRatesData
): IHAPFormattedAccommodationProductWithLiveRatesWithBookingComRates[] =>
  staticRates
    .map(accommodation => {
      // add in the booking com data first
      const bookingComRateForAccommodation =
        bookingComRatesData && bookingComRatesData.find(bc => bc.accommodationUuid === accommodation.uuid);
      if (bookingComRateForAccommodation) {
        accommodation.bookingComRate = { ...bookingComRateForAccommodation };
      }

      const liveRateAccommodation =
        Array.isArray(liveRates) && liveRates.find(item => accommodation.uuid === item.product.uuid);
      if (liveRateAccommodation) {
        accommodation.liveRates = liveRateAccommodation.rates;
      } else if (onlyShowLiveRates) {
        return null;
      }

      return accommodation;
    })
    .filter(accommodation => accommodation !== null);

const CurrencyFlag = ({ currencyCode, className }: { currencyCode: string; className?: string }) => {
  const countryCode = getCountryCodeForCurrency(currencyCode);
  if (!countryCode) {
    return <span className={`inline-block w-[18px] text-center ${className || ''}`}>•</span>;
  }
  return <Flag code={countryCode} className={`w-[18px] h-[13px] inline-block ${className || ''}`} />;
};

const BBCurrencySelector = () => {
  const { sortedCurrencies, selectedCurrencyData, onCurrencyChange } = useCurrencyConversion();

  const options: IMultiselectValueLabelPair[] = useMemo(() => {
    return sortedCurrencies.map(c => ({
      value: c.to.code,
      label: `${c.to.code} - ${c.to.name}`,
      iconHtml: <CurrencyFlag currencyCode={c.to.code} className="mr-[8px]" />,
      iconAlign: 'left' as const,
    }));
  }, [sortedCurrencies]);

  const selectedValuesRenderer = useCallback((label: string) => {
    return label.split(' - ')[0];
  }, []);

  if (!sortedCurrencies.length) {
    return null;
  }

  const selectedCurrency = selectedCurrencyData?.to.code || null;

  return (
    <Multiselect
      className="bg-ivory w-[150px]"
      itemsClassname="bg-ivory !w-[250px] right-0 left-auto"
      itemCtaClassName="hover:bg-gray-10"
      options={options}
      selectedValues={selectedCurrency ? [selectedCurrency] : []}
      onUpdate={(selectedValues: string[]) => onCurrencyChange(selectedValues[0] || null)}
      isSingleSelectMode
      hideCheckboxes
      isCloseOnSelect
      isIncludeClearButton
      placeholderText="Currency"
      selectedValuesRenderer={selectedValuesRenderer}
      showSelectedIcon
    />
  );
};

/**
 * the "left hand menu" of the booking builder
 * aka the hotel accommodations
 */
export const Rooms = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hotelUuid, className, lodgings } = props;

  const loadStatus = useSelector(HotelAccommodationProductSelectors.loadStatusSelector);

  const formattedAccommodationProducts: IHAPFormattedAccommodationProduct[] = useSelector(
    HotelAccommodationProductSelectors.getHotelAccommodationProductsSelector
  );

  const searchQuery = useSelector(FastSearchSelectors.offersQuerySelector);
  const currencyCode = useSelector(BookingsSelectors.getBookingCurrencySymbol) as TCurrencyCode;
  const bookingStatus = useSelector(BookingsSelectors.getBookingStatus);

  const staticRateAvailabilityNetworkRequests = useSelector(StaticRatesAvailabilitySelectors.networkRequestsSelector);
  const liveRates = useSelector(HotelAccommodationProductSelectors.getCurrentHotelAccommodationLiveRates);
  const liveRatesCurrency = useSelector(
    HotelAccommodationProductSelectors.getCurrentHotelAccommodationLiveRatesCurrency
  ) as TCurrencyCode;
  const roomsError = useSelector(
    HotelAccommodationProductSelectors.getCurrentHotelAccommodationProductsError
  ) as string;
  const isSr = useSelector(AuthSelectors.isSr);
  const company = useSelector(CompanyInfoSelectors.companyBookingBuilderSelector);
  const basketRateType = useSelector(BookingBuilderSelectors.bookingBasketRateType) as BasketRateTypes;

  const selectedTa = useSelector(AgentsSelectors.selectedTaSelector);
  const [filteredAccommodationProducts, setFilteredAccommodationProducts] = useState(formattedAccommodationProducts);
  const [selectedCategoryTypes, setSelectedCategoryTypes] = useState<string[]>([]);
  const [accommodationProductMarkup, setAccommodationProductMarkup] = useState<React.ReactElement[]>([]);
  const [onlyShowLiveRates, setOnlyShowLiveRates] = useState(false);
  const [includeStandardOccupancy, setIncludeStandardOccupancy] = useState(false);
  const [isAwaitingRangeData, setIsAwaitingRangeData] = useState(false);
  const { isMobile } = useCurrentWidth();
  const bookingComRatesData = useSelector(HotelAccommodationProductSelectors.bookingComRatesDataSelector);

  // Create stable hash of all search parameters that affect the API to prevent unnecessary useEffect fires
  const searchQueryHash = useMemo(
    () =>
      hash({
        startDate: searchQuery.startDate,
        endDate: searchQuery.endDate,
        clientCountryCode: searchQuery.clientCountryCode,
        lodgings: JSON.stringify(lodgings),
      }),
    [searchQuery.startDate, searchQuery.endDate, searchQuery.clientCountryCode, JSON.stringify(lodgings)]
  );

  if (roomsError) {
    Sentry.configureScope(function(scope) {
      scope.setTag('custom_source', 'hotelAccommodationProducts');
      scope.setTag('rooms_error', roomsError);
      scope.setTag('request_start_date', searchQuery.startDate);
      scope.setTag('request_end_date', searchQuery.endDate);
      scope.setTag('request_guest_ages', JSON.stringify(searchQuery.lodgings));
      scope.setTag('hotel_uuid', hotelUuid);
      scope.setTag('browser_url', window.location.href);
      Sentry.captureException(new Error(roomsError));
    });
  }

  // when the hotel or search parameters change, load new accommodation products
  useEffect(() => {
    async function load() {
      // request hotel accommodation products aka static rates
      // together with the live rates
      setIsAwaitingRangeData(true);
      await dispatch(
        HotelAccommodationProductActions.fetchHotelAccommodationProductsRequestAction(hotelUuid, lodgings, includeStandardOccupancy)
      );
    }

    load();
  }, [hotelUuid, searchQueryHash, includeStandardOccupancy]);

  // after we've loaded the accommodation products, we need to load the static rates availabilities
  // for the time covering the search range
  useEffect(() => {
    if (isEmpty(formattedAccommodationProducts)) {
      return;
    }
    // const cursor = new Date();
    // const startDate = formatDate(Date.UTC(cursor.getFullYear(), cursor.getMonth(), 1));
    // const endDate = formatDate(DateFNS.addDays(new Date(startDate), 62));
    const accommodationUuids = formattedAccommodationProducts.map(x => x.uuid);
    async function load() {
      // request hotel accommodation products aka static rates
      // together with the live rates
      setIsAwaitingRangeData(false);
      await dispatch(
        StaticRatesAvailabilityActions.fetchStaticRatesAvailabilityAggregateRequestAction(
          accommodationUuids,
          formatDate(DateFNS.startOfMonth(new Date(searchQuery.startDate))),
          formatDate(DateFNS.endOfMonth(DateFNS.addMonths(new Date(searchQuery.endDate), 1)))
        )
      );
    }
    load();
  }, [formattedAccommodationProducts]);

  // when the category changes, get new filtered accommodation products
  useEffect(() => {
    setFilteredAccommodationProducts(filterRoomsByCategoryType(formattedAccommodationProducts, selectedCategoryTypes));
  }, [formattedAccommodationProducts, selectedCategoryTypes]);

  // MARKUP
  useEffect(() => {
    const mergedAccommodationProducts = mergeAccommodationProductData(
      filteredAccommodationProducts,
      liveRates,
      onlyShowLiveRates,
      bookingComRatesData
    );

    const mergedAccommodationProductsSorted = sortBy(mergedAccommodationProducts, x => {
      const { fromPrice } = getFromPrices(x.staticRates, x.liveRates);
      const parsed = parseFloat(fromPrice || '');
      return isNaN(parsed) ? Infinity : parsed;
    });

    const handleAddRoom = accommodationProductData => {
      const { liveRateData } = accommodationProductData;
      const isLiveRate = has(liveRateData, 'externalRateId') && has(liveRateData, 'externalMealPlanCode');
      const liveRate = isLiveRate
        ? pick(liveRateData, ['externalRateId', 'externalMealPlanCode', 'externalMealPlanDescription', 'amount'])
        : undefined;
      const guestAges = isLiveRate
        ? omit(liveRateData, ['externalRateId', 'externalMealPlanCode', 'externalMealPlanDescription', 'amount'])
        : accommodationProductData.guestAges;
      const accommodationProduct = isLiveRate
        ? { ...accommodationProductData, defaultMealPlan: null, liveRate }
        : accommodationProductData;

      const includeServiceCharge = company?.serviceChargeOverride !== '0.00';

      // @ts-ignore legacy
      dispatch(
        BookingBuilderActions.addLodgingAction(
          accommodationProduct,
          searchQuery,
          hotelUuid,
          includeServiceCharge,
          guestAges
        )
      );
    };

    const markup = mergedAccommodationProductsSorted.map(accommodationProduct => {
      const roomInfoPdf =
        accommodationProduct.accommodationInformation?.length > 0
          ? accommodationProduct.accommodationInformation[0]
          : undefined;

      return (
        <AccommodationCard
          key={hash(accommodationProduct)}
          accommodationProduct={accommodationProduct}
          id={accommodationProduct.uuid}
          currencyCode={currencyCode}
          title={accommodationProduct.title}
          description={accommodationProduct.description}
          moreInformation={accommodationProduct.moreInformation}
          amenities={accommodationProduct.amenities}
          size={accommodationProduct.size}
          brochures={accommodationProduct.floorPlans}
          roomInfoPdf={roomInfoPdf}
          occupancy={accommodationProduct.occupancy}
          imageUri={pathOr(null, ['photos', 0, 'url'], accommodationProduct)}
          addRoom={handleAddRoom}
          staticRates={accommodationProduct.staticRates || []}
          liveRates={accommodationProduct.liveRates || []}
          liveRatesCurrency={liveRatesCurrency}
          basketRateType={basketRateType}
          isSr={isSr}
        />
      );
    });

    setAccommodationProductMarkup(markup);
  }, [
    searchQuery,
    filteredAccommodationProducts,
    currencyCode,
    bookingStatus,
    hotelUuid,
    liveRates,
    liveRatesCurrency,
    onlyShowLiveRates,
    basketRateType,
    bookingComRatesData,
  ]);

  const categoryTypes = useMemo(() => {
    const uniqueCategoryTypes = formattedAccommodationProducts.reduce((accum, room) => {
      accum[room.categoryType] = room.categoryType;
      return accum;
    }, {});
    const categoryTypes = Object.keys(uniqueCategoryTypes).map(categoryType => ({
      value: categoryType,
      label: categoryType,
    }));

    return categoryTypes;
  }, [formattedAccommodationProducts]);

  const toggleShowOnlyLiveRates = useCallback(() => {
    setOnlyShowLiveRates(!onlyShowLiveRates);
  }, [onlyShowLiveRates]);

  const toggleIncludeStandardOccupancy = useCallback(() => {
    setIncludeStandardOccupancy(!includeStandardOccupancy);
  }, [includeStandardOccupancy]);

  const isPaymentCurrencyEnabled = ParameterService.getParameters().ENABLE_PAYMENT_CURRENCY_CHANGE_TEMP === true;

  return (
    <RoomsStyles.StyledRooms className={className}>
      <div className="flex justify-between items-center">
        {/* @ts-ignore */}
        <RoomsStyles.Title>{t('labels.selectAvailableAccommodations')}</RoomsStyles.Title>
        {isPaymentCurrencyEnabled && <BBCurrencySelector />}
      </div>
      <RoomsStyles.AccommodationPricesInfo>
        {/* @ts-ignore */}
        {t('labels.accommodationPricesInfo')}
      </RoomsStyles.AccommodationPricesInfo>
      <div className="grid grid-cols-3 items-center gap-[16px] mb-[16px]">
        <Label
          className="text-left flex-row-reverse"
          text={t('labels.standardOccupancy')}
          textColor={includeStandardOccupancy ? theme.colors['bistre'] : theme.colors['flint']}
          inline
          wordWrap
        >
          <Checkbox
            label={t('labels.standardOccupancy')}
            onChange={toggleIncludeStandardOccupancy}
            checked={includeStandardOccupancy}
          />
        </Label>
        <div>
          {Array.isArray(liveRates) && liveRates.length > 0 && (
            <Label
              className="live-rates-filter text-left flex-row-reverse"
              text={t('labels.availableOnline')}
              textColor={onlyShowLiveRates ? theme.colors['bistre'] : theme.colors['flint']}
              inline
              wordWrap
            >
              <Checkbox
                label={t('labels.availableOnline')}
                onChange={toggleShowOnlyLiveRates}
                checked={onlyShowLiveRates}
              />
            </Label>
          )}
        </div>
        <div>
          {!isNilOrEmpty(categoryTypes) && (
            <Multiselect
              className="bg-ivory"
              itemsClassname="bg-ivory"
              itemCtaClassName="hover:bg-gray-10"
              options={categoryTypes}
              isCloseOnSelect={true}
              isIncludeClearButton={true}
              onUpdate={(sv: string[]) => {
                setSelectedCategoryTypes(sv);
              }}
              selectedValues={selectedCategoryTypes}
            />
          )}
        </div>
      </div>
      {roomsError && (
        // @ts-ignore
        <RoomsStyles.RoomsError>{t('errors.getCurrentHotelAccommodationProducts')}</RoomsStyles.RoomsError>
      )}
      {!roomsError && (
        <Loader
          isLoading={
            loadStatus === ENetworkRequestStatus.PENDING ||
            isAwaitingRangeData ||
            staticRateAvailabilityNetworkRequests.aggregate === ENetworkRequestStatus.PENDING
          }
          text={t('messages.gettingAccommodationProducts')}
        >
          <>
            {isNilOrEmpty(filteredAccommodationProducts) && (
              // @ts-ignore
              <RoomsStyles.NoResults>{t('labels.noRooms')}</RoomsStyles.NoResults>
            )}

            {filteredAccommodationProducts.length >= 1 && (
              <>
                <RoomsStyles.RoomsWrapper>{accommodationProductMarkup}</RoomsStyles.RoomsWrapper>
              </>
            )}
          </>
        </Loader>
      )}
    </RoomsStyles.StyledRooms>
  );
};

export default Rooms;
